import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import EnrollPopup from '../components/EnrollPopup';
import Calendar from '../components/Calendar';
import './OnlineCourseDetails.css';

const OnlineCourseDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showEnrollPopup, setShowEnrollPopup] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'calendar' | 'chapters'>('overview');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Mock data
  const course = {
    id: 1,
    title: 'Advanced Mathematics Live Course',
    instructor: 'Dr. Smith',
    category: 'Mathematics',
    price: 99,
    originalPrice: 149,
    rating: 4.8,
    reviewsCount: 245,
    studentsCount: 1200,
    totalSessions: 15,
    duration: '12 weeks',
    level: 'Intermediate',
    language: 'English',
    description: 'Learn advanced mathematical concepts through interactive live sessions. This comprehensive course covers all essential topics with real-time Q&A and problem solving.',
    image: '📚',
    isEnrolled: false,
    isFree: false,
    platform: 'BBB',
    chapters: [
      {
        id: 1,
        title: 'Introduction to Calculus',
        sessionsCount: 5,
        isCompleted: false,
        progress: 33,
        isFree: true,
        sessions: [
          { id: 1, title: 'Session 1: What is Calculus?', date: '2026-02-03', time: '10:00', status: 'past', hasRecording: true },
          { id: 2, title: 'Session 2: Understanding Limits', date: '2026-02-05', time: '14:00', status: 'past', hasRecording: true },
          { id: 3, title: 'Session 3: Introduction to Derivatives', date: '2026-02-07', time: '15:00', status: 'past', hasRecording: true },
          { id: 4, title: 'Session 4: Basic Differential Equations', date: '2026-02-10', time: '15:00', status: 'upcoming', hasRecording: false },
          { id: 5, title: 'Session 5: Advanced Techniques', date: '2026-02-10', time: '17:00', status: 'upcoming', hasRecording: false }
        ],
        materials: [
          { id: 1, type: 'PDF', title: 'Chapter Notes', size: '2.5 MB', icon: '📄' },
          { id: 2, type: 'Link', title: 'Additional Resources', icon: '🔗' }
        ],
        quiz: {
          id: 1,
          title: 'Chapter Quiz',
          questionsCount: 10,
          duration: '15 min',
          isCompleted: false
        }
      },
      {
        id: 2,
        title: 'Differential Equations',
        sessionsCount: 10,
        isCompleted: false,
        progress: 0,
        isFree: false,
        sessions: [
          { id: 7, title: 'Session 7: Advanced Integration', date: '2026-02-14', time: '10:00', status: 'upcoming', hasRecording: false },
          { id: 8, title: 'Session 8: Applications', date: '2026-02-14', time: '15:00', status: 'upcoming', hasRecording: false },
          { id: 9, title: 'Session 9: Real-world Problems', date: '2026-02-17', time: '15:00', status: 'upcoming', hasRecording: false },
          { id: 10, title: 'Session 10: Review Session', date: '2026-02-19', time: '14:00', status: 'upcoming', hasRecording: false }
        ],
        materials: [
          { id: 3, type: 'PDF', title: 'Chapter Notes', size: '3.2 MB', icon: '📄' }
        ],
        quiz: {
          id: 2,
          title: 'Chapter Quiz',
          questionsCount: 12,
          duration: '20 min',
          isCompleted: false
        }
      }
    ],
    allSessions: [
      // Sessions in current month (February 2026) - distributed across different days
      { id: 1, title: 'Session 1: What is Calculus?', date: '2026-02-03', time: '10:00', status: 'past' as const, chapterId: 1, hasRecording: true },
      { id: 2, title: 'Session 2: Understanding Limits', date: '2026-02-05', time: '14:00', status: 'past' as const, chapterId: 1, hasRecording: true },
      { id: 3, title: 'Session 3: Introduction to Derivatives', date: '2026-02-07', time: '15:00', status: 'past' as const, chapterId: 1, hasRecording: true },
      { id: 4, title: 'Session 4: Basic Differential Equations', date: '2026-02-10', time: '15:00', status: 'upcoming' as const, chapterId: 1, hasRecording: false },
      { id: 5, title: 'Session 5: Advanced Techniques', date: '2026-02-10', time: '17:00', status: 'upcoming' as const, chapterId: 1, hasRecording: false },
      { id: 6, title: 'Session 6: Integration Basics', date: '2026-02-12', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 7, title: 'Session 7: Advanced Integration', date: '2026-02-14', time: '10:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 8, title: 'Session 8: Applications', date: '2026-02-14', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 9, title: 'Session 9: Real-world Problems', date: '2026-02-17', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 10, title: 'Session 10: Review Session', date: '2026-02-19', time: '14:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 11, title: 'Session 11: Final Exam Prep', date: '2026-02-21', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 12, title: 'Session 12: Q&A Session', date: '2026-02-24', time: '10:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 13, title: 'Session 13: Extra Practice', date: '2026-02-24', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 14, title: 'Session 14: Final Review', date: '2026-02-26', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false },
      { id: 15, title: 'Session 15: Course Wrap-up', date: '2026-02-28', time: '15:00', status: 'upcoming' as const, chapterId: 2, hasRecording: false }
    ],
    reviews: [
      { id: 1, user: 'Ahmed', rating: 5, comment: 'Excellent course! Very well explained.', date: '2024-01-15' },
      { id: 2, user: 'Sara', rating: 4, comment: 'Good content but could use more examples.', date: '2024-01-10' }
    ]
  };

  const handleEnroll = () => {
    if (!course.isEnrolled) {
      setShowEnrollPopup(true);
    }
  };

  const handleSessionClick = (sessionId: number) => {
    navigate(`/online-course/${id}/session/${sessionId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getUpcomingSessions = () => {
    return course.allSessions.filter(s => s.status === 'upcoming');
  };

  const getPastSessions = () => {
    return course.allSessions.filter(s => s.status === 'past');
  };

  const getSessionsForDate = (date: string) => {
    return course.allSessions
      .filter(s => s.date === date)
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  return (
    <div className="online-course-details-page">
      <header className="online-course-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Course Details</h1>
        <button className="share-button">📤</button>
      </header>

      <div className="online-course-details-content">
        {/* Course Image/Header */}
        <div className="course-image-section">
          <div className="course-image-large">{course.image}</div>
          {course.isFree && <span className="free-badge">FREE</span>}
          {course.originalPrice > course.price && (
            <span className="discount-badge">
              {Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)}% OFF
            </span>
          )}
        </div>

        {/* Course Info */}
        <div className="course-info-section">
          <div className="course-header-info">
            <span className="course-category">{course.category}</span>
            <div className="course-rating">
              <span className="stars">⭐ {course.rating}</span>
              <span className="reviews-count">({course.reviewsCount})</span>
            </div>
          </div>
          <h2 className="course-title-large">{course.title}</h2>
          <p className="course-instructor-large">By {course.instructor}</p>
          
          <div className="course-meta-info">
            <div className="meta-item">
              <span className="meta-icon">👥</span>
              <span>{course.studentsCount} students</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">🔴</span>
              <span>{course.totalSessions} sessions</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">⏱️</span>
              <span>{course.duration}</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">💻</span>
              <span>{course.platform}</span>
            </div>
          </div>

          {/* Price */}
          <div className="course-price-section">
            {course.isFree ? (
              <span className="price-free">Free</span>
            ) : (
              <>
                <span className="price-current">${course.price}</span>
                {course.originalPrice > course.price && (
                  <span className="price-original">${course.originalPrice}</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="course-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            Calendar ({course.allSessions.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'chapters' ? 'active' : ''}`}
            onClick={() => setActiveTab('chapters')}
          >
            Chapters ({course.chapters.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <h3>About this course</h3>
              <p className="course-description">{course.description}</p>
              
              <h3>What you'll learn</h3>
              <ul className="learning-objectives">
                <li>✓ Master advanced calculus concepts</li>
                <li>✓ Solve complex differential equations</li>
                <li>✓ Apply integration techniques</li>
                <li>✓ Participate in interactive live sessions</li>
              </ul>

              <h3>Upcoming Sessions</h3>
              <div className="upcoming-sessions-preview">
                {getUpcomingSessions().slice(0, 3).map((session) => (
                  <div key={session.id} className="session-preview-item">
                    <div className="session-preview-date">
                      <span className="date-day">{new Date(session.date).getDate()}</span>
                      <span className="date-month">{new Date(session.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div className="session-preview-info">
                      <h4>{session.title}</h4>
                      <span className="session-preview-time">🕐 {session.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="calendar-content">
              {/* Calendar Component */}
              <Calendar
                sessions={course.allSessions.map(({ chapterId, ...session }) => ({
                  ...session,
                  status: session.status as 'upcoming' | 'past'
                }))}
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
              />

              {/* Selected Date Sessions */}
              {selectedDate && (
                <div className="selected-date-sessions">
                  <h3 className="selected-date-title">
                    Sessions on {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h3>
                  {getSessionsForDate(selectedDate).length > 0 ? (
                    <div className="sessions-list-selected">
                      {getSessionsForDate(selectedDate).map((session) => (
                        <div
                          key={session.id}
                          className={`session-card-selected ${session.status}`}
                        >
                          <div className="session-time-badge">
                            <span className="time-hour">{session.time}</span>
                          </div>
                          <div className="session-details-selected">
                            <h4 className="session-title-selected">{session.title}</h4>
                            <div className="session-status-selected">
                              {session.status === 'upcoming' ? (
                                <span className="status-upcoming">🔴 Upcoming</span>
                              ) : session.hasRecording ? (
                                <span className="status-recording">📹 Has Recording</span>
                              ) : (
                                <span className="status-past">Past Session</span>
                              )}
                            </div>
                          </div>
                          <div className="session-action-selected">
                            {session.status === 'upcoming' ? (
                              <button
                                className="join-btn-selected"
                                onClick={() => handleSessionClick(session.id)}
                              >
                                Join
                              </button>
                            ) : session.hasRecording ? (
                              <button
                                className="watch-btn-selected"
                                onClick={() => handleSessionClick(session.id)}
                              >
                                Watch
                              </button>
                            ) : (
                              <span className="no-recording-selected">No Recording</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-sessions-message">
                      <p>No sessions scheduled for this date.</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {activeTab === 'chapters' && (
            <div className="chapters-content-online">
              {course.chapters.map((chapter, index) => {
                const isLocked = !chapter.isFree && chapter.progress === 0 && !chapter.isCompleted;
                
                return (
                  <div 
                    key={chapter.id} 
                    className={`chapter-card-online ${isLocked ? 'locked' : ''} ${chapter.isCompleted ? 'completed' : ''}`}
                    onClick={(e) => {
                      if (!isLocked) {
                        // Prevent navigation if clicking on the button
                        if ((e.target as HTMLElement).closest('.play-icon-online')) {
                          return;
                        }
                        navigate(`/online-course/${id}/chapter/${chapter.id}`);
                      }
                    }}
                  >
                    <div className="chapter-left-online">
                      <div className={`chapter-number-online ${chapter.isCompleted ? 'completed' : ''}`}>
                        {chapter.isCompleted ? '✓' : index + 1}
                      </div>
                      <div className="chapter-info-online">
                        <h3 className="chapter-title-online">{chapter.title}</h3>
                        <div className="chapter-details-online">
                          <span className="chapter-sessions-count">🔴 {chapter.sessionsCount} sessions</span>
                          {chapter.isFree && <span className="free-badge-online">Free</span>}
                        </div>
                        {chapter.progress > 0 && !chapter.isCompleted && (
                          <div className="chapter-progress-online">
                            <div className="progress-bar-online">
                              <div 
                                className="progress-fill-online"
                                style={{ width: `${chapter.progress}%` }}
                              ></div>
                            </div>
                            <span className="progress-text-online">{chapter.progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="chapter-right-online">
                      {isLocked ? (
                        <span className="lock-icon-online">🔒</span>
                      ) : chapter.isCompleted ? (
                        <span className="check-icon-online">✓</span>
                      ) : (
                        <button 
                          className="play-icon-online"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/online-course/${id}/chapter/${chapter.id}`);
                          }}
                        >
                          ▶
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="course-action-bar">
        {course.isEnrolled ? (
          <button className="continue-button" onClick={() => setActiveTab('calendar')}>
            View Sessions
          </button>
        ) : (
          <button className="enroll-button-large" onClick={handleEnroll}>
            {course.isFree ? 'Enroll for Free' : `Enroll Now - $${course.price}`}
          </button>
        )}
      </div>

      <EnrollPopup 
        isOpen={showEnrollPopup} 
        onClose={() => setShowEnrollPopup(false)} 
      />
      <BottomNavigation />
    </div>
  );
};

export default OnlineCourseDetails;
